import { Entity } from '../types/entities/Entity';
import { PagedData } from '../types/PagedData';
import api from './api';

export class Client<E extends Entity> {
  protected urlPath: string;
  public getAllPath: string;

  constructor(urlPath: string, getAllPath?: string) {
    this.urlPath = urlPath;
    this.getAllPath = getAllPath || urlPath;
  }

  public async create(entity: Partial<E>): Promise<E> {
    return await api.post<Partial<E>, E>(this.urlPath, entity);
  }
  public async readOne(
    id: E['id'],
    searchParams?: URLSearchParams
  ): Promise<E> {
    const searchParamsCopy = new URLSearchParams(searchParams);
    return await api.get<E>(`${this.urlPath}/${id}?${searchParamsCopy}`);
  }

  public async readAll(
    page = 1,
    searchParams?: URLSearchParams,
    paginateSize?: string,
    filters?: any,
    sort?: any
  ): Promise<PagedData<E>> {
    const currentTimeInMillisec = Date.now();

    const searchParamsCopy = new URLSearchParams(searchParams);
    searchParamsCopy.append('page', page.toString());
    if (paginateSize !== undefined) {
      searchParamsCopy.append('paginateSize', paginateSize.toString());
    }
    if (filters !== undefined) {
      searchParamsCopy.append('filters', JSON.stringify(filters));
    }
    if (sort !== undefined) {
      searchParamsCopy.append('sort', JSON.stringify(sort));
    }
    const res = await api.get<PagedData<E>>(
      `${this.getAllPath}?${searchParamsCopy}`
    );

    return res;
  }
  public async readNames(
    searchParams?: URLSearchParams
  ): Promise<PagedData<E>> {
    const searchParamsCopy = new URLSearchParams(searchParams);

    const res = await api.get<PagedData<E>>(
      `${this.getAllPath}/names?${searchParamsCopy}`
    );

    return res;
  }
  public async readForListing(
    page = 1,
    searchParams?: URLSearchParams,
    paginateSize?: string,
    filters?: any,
    sort?: any
  ): Promise<PagedData<E>> {
    const currentTimeInMillisec = Date.now();

    const searchParamsCopy = new URLSearchParams(searchParams);
    searchParamsCopy.append('page', page.toString());
    if (paginateSize !== undefined) {
      searchParamsCopy.append('paginateSize', paginateSize.toString());
    }
    if (filters !== undefined) {
      searchParamsCopy.append('filters', JSON.stringify(filters));
    }
    if (sort !== undefined) {
      searchParamsCopy.append('sort', JSON.stringify(sort));
    }

    console.log(
      `Client.ts -> RunTime: ${Date.now() - currentTimeInMillisec}ms`
    );
    const res = await api.get<PagedData<E>>(
      `${this.getAllPath}?${searchParamsCopy}`
    );
    console.log(
      `Client.ts -> RunTime: ${Date.now() - currentTimeInMillisec}ms`
    );
    return res;
  }
  public async count(
    page = 1,
    searchParams?: URLSearchParams,
    paginateSize?: string,
    filters?: any,
    sort?: any
  ): Promise<PagedData<E>> {
    const searchParamsCopy = new URLSearchParams(searchParams);
    searchParamsCopy.append('page', page.toString());
    if (paginateSize !== undefined) {
      searchParamsCopy.append('paginateSize', paginateSize.toString());
    }
    if (filters !== undefined) {
      searchParamsCopy.append('filters', JSON.stringify(filters));
    }
    if (sort !== undefined) {
      searchParamsCopy.append('sort', JSON.stringify(sort));
    }
    return await api.get<PagedData<E>>(
      `${this.getAllPath}/count?${searchParamsCopy}`
    );
  }
  public async readAllWithAccordingTo(
    searchParams?: URLSearchParams
  ): Promise<PagedData<E>> {
    const searchParamsCopy = new URLSearchParams(searchParams);
    return await api.get<PagedData<E>>(
      `${this.getAllPath}?${searchParamsCopy}`
    );
  }

  public async update(id: E['id'], properties: Partial<E>): Promise<E> {
    return await api.patch<Partial<E>, E>(`${this.urlPath}/${id}`, properties);
  }
  public async restore(id: E['id'], properties: Partial<E>): Promise<E> {
    return await api.patch<Partial<E>, E>(`${this.urlPath}/${id}/restore`, properties);
  }

  public async lock(id: E['id'], properties: any): Promise<E> {
    const searchParamsCopy = new URLSearchParams(properties);

    return await api.get(`/lock/${id}`, searchParamsCopy);
  }

  public async readLock(properties: any): Promise<E> {
    const searchParamsCopy = new URLSearchParams(properties);

    return await api.get(`/locks`, searchParamsCopy);
  }
  public async isLock(id: E['id'], properties: any): Promise<E> {
    const searchParamsCopy = new URLSearchParams(properties);

    return await api.get(`/is-lock/${id}`, searchParamsCopy);
  }

  public async kick(id: E['id'], properties: any): Promise<E> {
    const searchParamsCopy = new URLSearchParams(properties);

    return await api.get(`/kick/${id}`, searchParamsCopy);
  }

  public async kickUserEdit(id: E['id'], properties: any): Promise<E> {
    return await api.post(`/kick/${id}`, properties);
  }

  public async deleteLock(id?: E['id'], properties?: object): Promise<void> {
    await api.delete(`/lock/${id}`, properties);
  }

  public async updateOption(id: E['id'], properties: object): Promise<E> {
    return await api.patch<Partial<E>, E>(
      `${this.urlPath}/update-group-size/${id}`,
      properties
    );
  }

  public async delete(id: E['id'], properties?: object): Promise<void> {
    await api.delete(`${this.urlPath}/${id}`, properties);
  }

  public async deleteWithFiles(id: E['id'], properties?: object): Promise<void> {
    await api.deleteWithFiles(`${this.urlPath}/${id}`, properties);
  }

  public async duplicate(id: E['id']): Promise<void> {
    return await api.post(`${this.urlPath}/${id}/duplicates`, undefined);
  }
}
